<script setup lang="ts">
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import AuthLayout from '@/layouts/AuthLayout.vue'
import logoRobot from '@/assets/logo-home-bzuco.png'
import footerRobot from '@/assets/footer-robot.png'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const error = ref<string | null>(null)
const submitting = ref(false)

async function submit() {
    submitting.value = true
    error.value = null
    try {
        const r = await auth.login({ username: username.value, password: password.value })
        if (r.ok) {
            const next = (route.query.next as string) || '/'
            router.push(next)
        } else {
            // Backend's AdminUserLoginMutation returns only { ok, user } — no message field.
            // ok:false from this mutation almost always means bad credentials
            // (other failures throw GraphQL errors handled in catch).
            error.value = r.message ?? 'Nesprávný e-mail nebo heslo.'
        }
    } catch (e: unknown) {
        error.value = (e as Error).message
    } finally {
        submitting.value = false
    }
}
</script>

<template>
    <AuthLayout>
        <div class="flex flex-col items-center w-full px-4">
            <div class="flex items-center gap-3 mb-6">
                <img :src="logoRobot" alt="" class="h-12 w-12 object-cover object-left" />
                <span class="text-2xl font-semibold text-ink">Inspector</span>
            </div>
            <form class="bg-white p-6 md:p-8 rounded-lg shadow w-full max-w-96" @submit.prevent="submit">
                <h1 class="text-xl font-semibold mb-6">Přihlášení</h1>
                <label class="block mb-3">
                    <span class="text-sm text-gray-700">E-mail uživatele</span>
                    <input
                        v-model="username"
                        type="email"
                        required
                        autocomplete="email"
                        class="w-full mt-1 px-3 py-2.5 text-base border border-gray-300 rounded"
                    />
                </label>
                <label class="block mb-4">
                    <span class="text-sm text-gray-700">Heslo</span>
                    <input
                        v-model="password"
                        type="password"
                        required
                        autocomplete="current-password"
                        class="w-full mt-1 px-3 py-2.5 text-base border border-gray-300 rounded"
                    />
                </label>
                <p v-if="error" class="text-sm text-red-600 mb-3">{{ error }}</p>
                <button
                    type="submit"
                    :disabled="submitting"
                    class="w-full py-2.5 md:py-2 bg-teal-600 text-white rounded disabled:opacity-50"
                >
                    {{ submitting ? 'Přihlašuji...' : 'Přihlásit' }}
                </button>
            </form>
        </div>
        <img
            :src="footerRobot"
            alt=""
            class="absolute bottom-0 left-1/2 -translate-x-1/2 h-28 sm:h-40 pointer-events-none select-none"
        />
    </AuthLayout>
</template>
